import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Pagination, Response } from '@ui/src';
import { Channel, ChannelStatus, ChannelType, Employee } from '../../types';
import { messagesListApi } from '../services';

export interface MessagesGeneralState {
  mainChannels?: Channel[];
  mainChannelsByTypeIsLoading?: boolean;
  requestChannels?: Channel[];
  requestChannelsByTypeIsLoading?: boolean;
  mainChannelsPagination?: Pagination;
  requestChannelsPagination?: Pagination;
  assignedToMe?: boolean;
  currentQuery: string;
  mainCnannelsLoadingErrorVisible?: boolean;
  requestChannelsLoadingErrorVisible?: boolean;
  selectedChannel?: Channel;
  mainChannelsErrorText?: string;
  requestChannelsErrorText?: string;
  selectedChannelTab?: 'new' | 'in-progress' | 'completed' | 'all';
  scrolled?: boolean;
  newMessageCount: number;
  employee?: Employee;
  accessToken?: string;
  channelsInfo?: {
    mainChannels: number;
    requestChannels: number;
  };
  searchQuery?: string;
}

const initialState: MessagesGeneralState = {
  assignedToMe: false,
  mainChannelsByTypeIsLoading: false,
  requestChannelsByTypeIsLoading: false,
  currentQuery: '',
  newMessageCount: 0,
};

export const messagesGeneralSlice = createSlice({
  name: 'messagesGeneral',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    setScrolled: (state, action: PayloadAction<boolean>) => {
      state.scrolled = action.payload;
    },
    setNewMessageCount: (state, action: PayloadAction<number>) => {
      state.newMessageCount = action.payload;
    },
    setSelectedChannel: (state, action: PayloadAction<Channel | undefined>) => {
      state.selectedChannel = action.payload;
    },
    setSelectedChannelTab: (
      state,
      action: PayloadAction<'new' | 'in-progress' | 'completed' | 'all'>,
    ) => {
      state.selectedChannelTab = action.payload;
    },
    setAssigned: (state, action: PayloadAction<boolean>) => {
      state.assignedToMe = action.payload;
    },
    setMainChannelsByTypeIsLoading: (state, action: PayloadAction<boolean>) => {
      state.mainChannelsByTypeIsLoading = action.payload;
    },
    setRequestChannelsByTypeIsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.requestChannelsByTypeIsLoading = action.payload;
    },
    setMainChannelsLoadingErrorVisible: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.mainCnannelsLoadingErrorVisible = action.payload;
    },
    setRequestChannelsLoadingErrorVisible: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.requestChannelsLoadingErrorVisible = action.payload;
    },
    setRequestChannelsErrorText: (state, action: PayloadAction<string>) => {
      state.requestChannelsErrorText = action.payload;
    },
    setCurrentQuery: (state, action: PayloadAction<string>) => {
      state.currentQuery = action.payload;
    },
    setMainChannels: (state, action: PayloadAction<Channel[]>) => {
      state.mainChannels = action.payload;
    },
    setRequestChannels: (state, action: PayloadAction<Channel[]>) => {
      state.requestChannels = action.payload;
    },
    setRequestChannelsPagination: (
      state,
      action: PayloadAction<Pagination>,
    ) => {
      state.requestChannelsPagination = action.payload;
    },
    setMainChannelsPagination: (state, action: PayloadAction<Pagination>) => {
      state.mainChannelsPagination = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      messagesListApi.endpoints.getMainChannels.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          state.mainCnannelsLoadingErrorVisible = true;

          if (action?.payload?.data && action?.payload?.data?.message) {
            if (Array.isArray(action?.payload?.data?.message)) {
              if (action?.payload?.data?.message[0].constraints) {
                state.mainChannelsErrorText =
                  action?.payload?.data?.message[0].constraints &&
                  Object.values(
                    action?.payload?.data?.message[0].constraints,
                  )[0];
              } else {
                state.mainChannelsErrorText =
                  'Internal server error. Try again later';
              }
            } else if (typeof action?.payload?.data?.message === 'string') {
              state.mainChannelsErrorText = action?.payload?.data?.message;
            }
          } else {
            state.mainChannelsErrorText =
              'Internal server error. Try again later';
          }

          state.mainChannels = undefined;
          state.mainChannelsPagination = undefined;
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getSelectedChannel.matchFulfilled,
      (state, action: PayloadAction<{ data: Channel }>) => {
        state.selectedChannel = action.payload.data;
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMainChannels.matchFulfilled,
      (state, action: any & PayloadAction<Response<Channel>>) => {
        state.mainChannels = action.payload.data.edges.map(
          (item: { node: Channel }) => item.node,
        );
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMainChannelsByType.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          state.mainCnannelsLoadingErrorVisible = true;

          if (action?.payload?.data && action?.payload?.data?.message) {
            if (Array.isArray(action?.payload?.data?.message)) {
              if (action?.payload?.data?.message[0].constraints) {
                state.mainChannelsErrorText =
                  action?.payload?.data?.message[0].constraints &&
                  Object.values(
                    action?.payload?.data?.message[0].constraints,
                  )[0];
              } else {
                state.mainChannelsErrorText =
                  'Internal server error. Try again later';
              }
            } else if (typeof action?.payload?.data?.message === 'string') {
              state.mainChannelsErrorText = action?.payload?.data?.message;
            }
          } else {
            state.mainChannelsErrorText =
              'Internal server error. Try again later';
          }

          state.mainChannels = undefined;
          state.mainChannelsPagination = undefined;
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessages.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          if (state.selectedChannel?.channelType === ChannelType.MAIN) {
            state.mainCnannelsLoadingErrorVisible = true;
          } else {
            state.requestChannelsLoadingErrorVisible = true;
          }

          if (action?.payload?.data && action?.payload?.data?.message) {
            if (Array.isArray(action?.payload?.data?.message)) {
              if (action?.payload?.data?.message[0].constraints) {
                const errorText =
                  action?.payload?.data?.message[0].constraints &&
                  Object.values(
                    action?.payload?.data?.message[0].constraints,
                  )[0];

                if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                  state.mainChannelsErrorText = errorText;
                } else {
                  state.requestChannelsErrorText = errorText;
                }
              } else {
                const errorText = 'Internal server error. Try again later';

                if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                  state.mainChannelsErrorText = errorText;
                } else {
                  state.requestChannelsErrorText = errorText;
                }
              }
            } else if (typeof action?.payload?.data?.message === 'string') {
              if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                state.mainChannelsErrorText = action?.payload?.data?.message;
              } else {
                state.requestChannelsErrorText = action?.payload?.data?.message;
              }
            }
          } else {
            const errorText = 'Internal server error. Try again later';

            if (state.selectedChannel?.channelType === ChannelType.MAIN) {
              state.mainChannelsErrorText = errorText;
            } else {
              state.requestChannelsErrorText = errorText;
            }
          }
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMainChannelsByType.matchFulfilled,
      (state, action: any & PayloadAction<Response<Channel>>) => {
        if (!action?.meta?.arg?.originalArgs?.skip) {
          state.mainChannels = action.payload.data.edges.map(
            (item: { node: Channel }) => item.node,
          );
        } else {
          const nextMainChannels = ([] as Channel[]).concat(
            state.mainChannels as Channel[],
          );

          action.payload.data.edges.forEach((item: { node: Channel }) => {
            nextMainChannels.push(item.node);
          });

          state.mainChannels = nextMainChannels;
        }

        state.mainChannelsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getRequestsChannels.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          state.requestChannelsLoadingErrorVisible = true;

          if (action?.payload?.data && action?.payload?.data?.message) {
            if (Array.isArray(action?.payload?.data?.message)) {
              if (action?.payload?.data?.message[0].constraints) {
                state.requestChannelsErrorText =
                  action?.payload?.data?.message[0].constraints &&
                  Object.values(
                    action?.payload?.data?.message[0].constraints,
                  )[0];
              } else {
                state.requestChannelsErrorText =
                  'Internal server error. Try again later';
              }
            } else if (typeof action?.payload?.data?.message === 'string') {
              state.requestChannelsErrorText = action?.payload?.data?.message;
            }
          } else {
            state.requestChannelsErrorText =
              'Internal server error. Try again later';
          }

          state.requestChannels = undefined;
          state.requestChannelsPagination = undefined;
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.changeChatStatus.matchFulfilled,
      (state, action: any & PayloadAction<{ data: Channel }>) => {
        if (
          state.selectedChannelTab !== 'completed' &&
          state.selectedChannelTab !== 'all'
        ) {
          if (state.selectedChannel?.channelType === ChannelType.MAIN) {
            state.mainChannels = state?.mainChannels?.filter(
              (item) => item.id !== action?.meta?.arg?.originalArgs?.id,
            );
          } else {
            state.requestChannels = state?.requestChannels?.filter(
              (item) => item.id !== action?.meta?.arg?.originalArgs?.id,
            );
          }
        } else if (
          state.selectedChannelTab === 'completed' ||
          state.selectedChannelTab === 'all'
        ) {
          if (state.selectedChannel?.channelType === ChannelType.MAIN) {
            state.mainChannels = state?.mainChannels?.map((item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            });
          } else {
            state.requestChannels = state?.requestChannels?.map((item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            });
          }
        }

        if (
          action?.meta?.arg?.originalArgs?.body.status === ChannelStatus.CLOSED
        ) {
          state.selectedChannelTab = undefined;
        } else {
          state.selectedChannel = action.payload.data;
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.changeChatStatus.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          if (state.selectedChannel?.channelType === ChannelType.MAIN) {
            state.mainCnannelsLoadingErrorVisible = true;
          } else {
            state.requestChannelsLoadingErrorVisible = true;
          }

          if (action?.payload?.data && action?.payload?.data?.message) {
            if (Array.isArray(action?.payload?.data?.message)) {
              if (action?.payload?.data?.message[0].constraints) {
                const errorText =
                  action?.payload?.data?.message[0].constraints &&
                  Object.values(
                    action?.payload?.data?.message[0].constraints,
                  )[0];

                if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                  state.mainChannelsErrorText = errorText;
                } else {
                  state.requestChannelsErrorText = errorText;
                }
              } else {
                const errorText = 'Internal server error. Try again later';

                if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                  state.mainChannelsErrorText = errorText;
                } else {
                  state.requestChannelsErrorText = errorText;
                }
              }
            } else if (typeof action?.payload?.data?.message === 'string') {
              if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                state.mainChannelsErrorText = action?.payload?.data?.message;
              } else {
                state.requestChannelsErrorText = action?.payload?.data?.message;
              }
            }
          } else {
            const errorText = 'Internal server error. Try again later';

            if (state.selectedChannel?.channelType === ChannelType.MAIN) {
              state.mainChannelsErrorText = errorText;
            } else {
              state.requestChannelsErrorText = errorText;
            }
          }
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getRequestsChannels.matchFulfilled,
      (state, action: PayloadAction<Response<Channel>>) => {
        state.requestChannels = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessagesChannelInfo.matchFulfilled,
      (
        state,
        action: PayloadAction<{
          data: { mainChannels: number; requestChannels: number };
        }>,
      ) => {
        state.channelsInfo = action.payload.data;
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.assignEmployeeToChat.matchFulfilled,
      (state, action: any & PayloadAction<{ data: Channel }>) => {
        if (action?.meta?.arg?.originalArgs?.body?.employeeId) {
          if (
            state.requestChannels?.find(
              (item) => item.id === action.payload.data.id,
            )
          ) {
            state.requestChannels = state?.requestChannels?.map((item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            });
          }

          if (
            state.mainChannels?.find(
              (item) => item.id === action.payload.data.id,
            )
          ) {
            state.mainChannels = state?.mainChannels?.map((item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            });
          }

          state.selectedChannel = action.payload.data;
        } else {
          state.selectedChannel = undefined;

          if (
            state.requestChannels?.find(
              (item) => item.id === action.payload.data.id,
            )
          ) {
            state.requestChannels = state?.requestChannels?.filter(
              (item) => item.id !== action.payload.data.id,
            );

            state.requestChannelsPagination = {
              ...state.requestChannelsPagination,
              totalCount:
                (state.requestChannelsPagination?.totalCount as number) - 1,
            } as Pagination;
          }

          if (
            state.mainChannels?.find(
              (item) => item.id === action.payload.data.id,
            )
          ) {
            state.mainChannels = state?.mainChannels?.filter(
              (item) => item.id !== action.payload.data.id,
            );

            state.mainChannelsPagination = {
              ...state.mainChannelsPagination,
              totalCount:
                (state.mainChannelsPagination?.totalCount as number) - 1,
            } as Pagination;
          }
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getRequestsChannelsByType.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          state.requestChannelsLoadingErrorVisible = true;

          if (action?.payload?.data && action?.payload?.data?.message) {
            if (Array.isArray(action?.payload?.data?.message)) {
              if (action?.payload?.data?.message[0].constraints) {
                state.requestChannelsErrorText =
                  action?.payload?.data?.message[0].constraints &&
                  Object.values(
                    action?.payload?.data?.message[0].constraints,
                  )[0];
              } else {
                state.requestChannelsErrorText =
                  'Internal server error. Try again later';
              }
            } else if (typeof action?.payload?.data?.message === 'string') {
              state.requestChannelsErrorText = action?.payload?.data?.message;
            }
          } else {
            state.requestChannelsErrorText =
              'Internal server error. Try again later';
          }

          state.requestChannels = undefined;
          state.requestChannelsPagination = undefined;
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.moveDocumentsToCustomer.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          if (state.selectedChannel?.channelType === ChannelType.MAIN) {
            state.mainCnannelsLoadingErrorVisible = true;
          } else {
            state.requestChannelsLoadingErrorVisible = true;
          }

          if (action?.payload?.data && action?.payload?.data?.message) {
            if (Array.isArray(action?.payload?.data?.message)) {
              if (action?.payload?.data?.message[0].constraints) {
                const errorText =
                  action?.payload?.data?.message[0].constraints &&
                  Object.values(
                    action?.payload?.data?.message[0].constraints,
                  )[0];

                if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                  state.mainChannelsErrorText = errorText;
                } else {
                  state.requestChannelsErrorText = errorText;
                }
              } else {
                const errorText = 'Internal server error. Try again later';

                if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                  state.mainChannelsErrorText = errorText;
                } else {
                  state.requestChannelsErrorText = errorText;
                }
              }
            } else if (typeof action?.payload?.data?.message === 'string') {
              if (state.selectedChannel?.channelType === ChannelType.MAIN) {
                state.mainChannelsErrorText = action?.payload?.data?.message;
              } else {
                state.requestChannelsErrorText = action?.payload?.data?.message;
              }
            }
          } else {
            const errorText = 'Internal server error. Try again later';

            if (state.selectedChannel?.channelType === ChannelType.MAIN) {
              state.mainChannelsErrorText = errorText;
            } else {
              state.requestChannelsErrorText = errorText;
            }
          }
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getRequestsChannelsByType.matchFulfilled,
      (state, action: any & PayloadAction<Response<Channel>>) => {
        if (!action?.meta?.arg?.originalArgs?.skip) {
          state.requestChannels = action.payload.data.edges.map(
            (item: { node: Channel }) => item.node,
          );
        } else {
          const nextRequestsChannels = ([] as Channel[]).concat(
            state.requestChannels as Channel[],
          );

          action.payload.data.edges.forEach((item: { node: Channel }) => {
            nextRequestsChannels.push(item.node);
          });

          state.requestChannels = nextRequestsChannels;
        }

        state.requestChannelsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessagingCurrentEmployee.matchFulfilled,
      (state, action: PayloadAction<{ data: Employee }>) => {
        state.employee = action.payload.data;
      },
    );
  },
});

export const {
  setAssigned,
  setCurrentQuery,
  setMainChannelsByTypeIsLoading,
  setRequestChannelsByTypeIsLoading,
  setMainChannelsLoadingErrorVisible,
  setRequestChannelsLoadingErrorVisible,
  setSelectedChannel,
  setSelectedChannelTab,
  setMainChannels,
  setRequestChannels,
  setScrolled,
  setNewMessageCount,
  setAccessToken,
  setRequestChannelsPagination,
  setMainChannelsPagination,
  setRequestChannelsErrorText,
  setSearchQuery,
} = messagesGeneralSlice.actions;

export default messagesGeneralSlice.reducer;
